import AddConstraintGoal from '@/components/Base/modals/AddConstraintModal'
import { LoadConstraintsDialog } from '@/components/Base/modals/LoadConstraintsDialog'
import { SaveConstraintsDialog } from '@/components/Base/modals/SaveConstraintsDialog'
import { AccordionTitle } from '@/components/Shared/AccordionComponent'
import {
  AccordionKeys,
  ConstraintOperation,
} from '@/enums/portfolio-optimization.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  IConstraint,
  IConstraintTemplate,
  ICreateConstraintsTemplateRequestBody,
  IUpdateConstraintsTemplateRequestBody,
} from '@/interfaces/portfolioOptimization.interface'
import {
  createConstraintTemplate,
  getConstraintTemplateDetails,
  getConstraintTemplates,
  updateConstraintTemplate,
} from '@/services/portfolio-optimization/constraints.service'
import {
  addConstraint,
  resetConstraints,
  toggleExpanded,
  updateTemplate,
} from '@/states/optimizationConstraint.slice'
import {
  setAccordionComplete,
  setConstraintItems,
} from '@/states/portfolioOptmization.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import { SEMANTIC_COLOR_PALETTE } from '@/theme/theme-config'
import { AccordionActions, Box, Button, Typography } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import StatusIndicator from '../../StatusIndicator'
import OptimizationConstraintGrid from './OptimizationConstraintGrid'
const OptimizationGoalsAccordion: React.FC = () => {
  const dispatch = useDispatch()
  const isAccordionComplete = useAppSelector(
    (s) =>
      s.portfolioOptimization.accordions[
        AccordionKeys.OPTIMIZATION_CONSTRAINTS
      ],
  )
  const currentTemplate =
    useAppSelector((s) => s.optimizationConstraint.template) || {}

  const [constraintTemplates, setConstraintTemplates] = useState<
    IConstraintTemplate[]
  >([])
  const [selectedConstraintTemplate, setSelectedConstraintTemplate] =
    useState<IConstraintTemplate | null>(null)
  const [selectedConstraintTemplateId, setSelectedConstraintTemplateId] =
    useState<string | null>(currentTemplate?.id || null)
  const [constraintsTemplateName, setConstraintsTemplateName] = useState(
    currentTemplate?.description,
  )

  // Modals & Dialogs
  const [constraintGoalModal, setConstraintGoalModal] = useState(false)
  const [loadTemplateModal, setLoadTemplateModal] = useState(false)
  const [saveTemplateModal, setSaveTemplateModal] = useState(false)

  // Selector
  const optimizationConstraintState = useAppSelector(
    (s) => s.optimizationConstraint,
  )

  const handleError = (error: any, context: string) => {
    const errorMessage =
      error?.response?.data?.message || error?.message || 'An error occurred'
    dispatch(
      showSnackbar({
        message: `${context}: ${errorMessage}`,
        severity: 'error',
      }),
    )
    console.error(`${context} error:`, error)
  }

  const applyConstraintsAndGoal = useCallback(() => {
    // TO Implement Apply to optimization template
    dispatch(toggleExpanded())
    dispatch(setConstraintItems(currentTemplate.constraints))
    dispatch(
      setAccordionComplete({
        accordionType: AccordionKeys.OPTIMIZATION_CONSTRAINTS,
        isComplete: true,
      }),
    )
  }, [dispatch, toggleExpanded, currentTemplate])

  const handleConstraintGoalModalSave = useCallback(
    (constraint: IConstraint) => {
      dispatch(addConstraint(constraint))
      setConstraintGoalModal(false)
    },
    [dispatch],
  )

  // Memoize the AddConstraintGoal component
  const MemoizedAddConstraintGoal = useMemo(() => {
    return React.memo(AddConstraintGoal)
  }, [])

  // Use memoized components

  const loadSelectedTemplate = async (templateId: string) => {
    if (templateId && templateId !== '') {
      try {
        const template = await getConstraintTemplateDetails(templateId)
        if (template) {
          setSelectedConstraintTemplate({
            id: template.id,
            description: template.description,
          })
          setSelectedConstraintTemplateId(template.id)
          setConstraintsTemplateName(template.description)
          dispatch(
            updateTemplate({
              id: template.id,
              description: template?.description,
              constraints:
                template.constraints?.map((item) => {
                  const rowKey = item.rowKey || uuidv4()
                  return {
                    ...item,
                    rowKey,
                  }
                }) || [],
            }),
          )
          if (!template.constraints || template.constraints.length === 0) {
            dispatch(
              setAccordionComplete({
                accordionType: AccordionKeys.OPTIMIZATION_CONSTRAINTS,
                isComplete: false,
              }),
            )
          }
        }
      } catch (error) {
        handleError(
          error,
          `Failed to load constraint template with id ${templateId}`,
        )
      }
      setLoadTemplateModal(false)
    }
  }

  const handleConstraintTemplateSave = async () => {
    const config: any = {}
    const isNew: boolean = Boolean(!selectedConstraintTemplate)

    if (selectedConstraintTemplate) {
      config.id = selectedConstraintTemplate.id
      config.description = selectedConstraintTemplate.description
      config.constraints =
        optimizationConstraintState.template?.constraints || []
    } else {
      config.description = constraintsTemplateName
      config.constraints =
        optimizationConstraintState.template?.constraints || []
    }
    if (constraintsTemplateName && constraintsTemplateName !== '') {
      config.description = constraintsTemplateName
    }

    if (isNew) {
      createNewConstraintTemplate(config)
    } else {
      updateExistingConstraintTemplate(config.id, config)
    }
    setSaveTemplateModal(false)
  }

  const createNewConstraintTemplate = async (
    config: ICreateConstraintsTemplateRequestBody,
  ) => {
    try {
      const res = await createConstraintTemplate(config)
      if (res.success) {
        loadSelectedTemplate(res.result)

        dispatch(
          showSnackbar({
            message: `Constraint template created successfully with id ${res.result}`,
            severity: 'success',
          }),
        )
      } else {
        throw new Error('Failed to create constraint template')
      }
    } catch (error) {
      handleError(error, `Failed to create constraint template`)
    } finally {
      fetchConstraintTemplates()
    }
  }

  const updateExistingConstraintTemplate = async (
    id: string,
    config: IUpdateConstraintsTemplateRequestBody,
  ) => {
    try {
      const res = await updateConstraintTemplate(id, config)
      if (res.success) {
        loadSelectedTemplate(res.result)
        dispatch(
          showSnackbar({
            message: `Constraint template updated successfully with id ${res.result}`,
            severity: 'success',
          }),
        )
      } else {
        throw new Error('Failed to update constraint template')
      }
    } catch (error) {
      handleError(error, `Failed to update constraint template with id ${id}`)
    } finally {
      fetchConstraintTemplates()
    }
  }

  const fetchConstraintTemplates = useCallback(async () => {
    try {
      const response = await getConstraintTemplates()
      setConstraintTemplates(response)
    } catch (error) {
      handleError(error, 'Failed to load constraint templates')
    }
  }, [])

  useEffect(() => {
    if (loadTemplateModal) {
      fetchConstraintTemplates()
    }
  }, [loadTemplateModal])

  useEffect(() => {
    if (selectedConstraintTemplate) {
      setConstraintsTemplateName(selectedConstraintTemplate.description)
    }
  }, [selectedConstraintTemplate])

  return (
    <Accordion
      expanded={optimizationConstraintState.isExpanded}
      onChange={() => dispatch(toggleExpanded())}
    >
      <AccordionTitle aria-controls='panel4-content' id='panel4-header'>
        <Typography variant='h3'>Optimization Goals and Constraints</Typography>
        <StatusIndicator isComplete={isAccordionComplete} />
      </AccordionTitle>
      <AccordionDetails
        sx={{
          display: 'flex',
          gap: 1,
          flexDirection: 'column',
          marginTop: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h5'>
            Active Portfolio Optimization Constraints/Goals
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size='small'
              variant='outlined'
              data-testid='add-constraint-goal-button'
              onClick={() => setConstraintGoalModal(true)}
            >
              Add New Constraints/Goals
            </Button>
            <Button
              size='small'
              variant='outlined'
              onClick={() => setLoadTemplateModal(true)}
            >
              Load Screen Constraint
            </Button>
            <Button
              size='small'
              variant='outlined'
              onClick={() => setSaveTemplateModal(true)}
            >
              Save Current Screen Constraint
            </Button>
          </Box>
        </Box>
        <OptimizationConstraintGrid
          constraintList={optimizationConstraintState.template?.constraints}
        />
      </AccordionDetails>
      <AccordionActions
        sx={{
          padding: 2,
          borderTop: '1px solid',
          borderTopColor: SEMANTIC_COLOR_PALETTE.border.light,
        }}
      >
        <Button
          size='small'
          variant='text'
          disabled={!optimizationConstraintState.template?.constraints?.length}
          onClick={() => dispatch(resetConstraints())}
        >
          Reset
        </Button>
        <Button
          size='small'
          variant='contained'
          disabled={!optimizationConstraintState.template?.constraints?.length}
          onClick={applyConstraintsAndGoal}
        >
          Apply Constraints and Goals
        </Button>

        {/* Load Constraints */}
        <LoadConstraintsDialog
          loadTemplateModal={loadTemplateModal}
          setLoadTemplateModal={setLoadTemplateModal}
          constraintTemplates={constraintTemplates}
          loadSelectedTemplate={loadSelectedTemplate}
          selectedConstraintTemplateId={selectedConstraintTemplateId}
          setSelectedConstraintTemplateId={setSelectedConstraintTemplateId}
        />

        {/* Save Constraints */}
        <SaveConstraintsDialog
          saveTemplateModal={saveTemplateModal}
          setSaveTemplateModal={setSaveTemplateModal}
          constraintsTemplateName={constraintsTemplateName}
          setConstraintsTemplateName={setConstraintsTemplateName}
          handleConstraintTemplateSave={handleConstraintTemplateSave}
        />

        {constraintGoalModal && (
          <MemoizedAddConstraintGoal
            mode={ConstraintOperation.ADD}
            isOpen={constraintGoalModal}
            onApplyConstraints={handleConstraintGoalModalSave}
            onClose={() => setConstraintGoalModal(false)}
          />
        )}
      </AccordionActions>
    </Accordion>
  )
}

export default OptimizationGoalsAccordion
